import { useEffect, useState } from "react";
import { MessageSquare, Save, X, Pencil, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

/**
 * Reusable inline editor for adding a short note to a specific chart or a
 * general scenario summary. Persists via a caller-provided `onSave(text)`
 * async callback so the same component works for scenario-level charts
 * (PATCH /scenarios/{id}/description) and the club-level comparison chart
 * (PATCH /clubs/{id}/compare-description).
 *
 * Props:
 *  - value: current text (string, may be empty)
 *  - onSave: async (text) => void — throws on error
 *  - label: text on the toggle button ("Beschrijving")
 *  - dialogTitle: dialog headline (e.g. "Beschrijving bij Kasstroomtraject")
 *  - testid: base test-id (e.g. "chart-desc-cash_flow")
 *  - readOnly: when true, only render the preview if a value exists (no edit button)
 *  - maxLength: default 500
 *  - onGenerate: optional async () => string — enables an "AI genereer" button that fills the textarea
 *  - generateLabel: caption on the generate button (default "AI genereer")
 */
export default function ChartDescriptionEditor({
  value = "",
  onSave,
  label,
  editLabel,
  dialogTitle,
  testid = "chart-desc",
  readOnly = false,
  maxLength = 500,
  onGenerate,
  generateLabel,
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { setDraft(value || ""); }, [value]);

  const trimmed = (draft || "").trim();
  const hasText = Boolean((value || "").trim());
  const btnLabel = label || t("Beschrijving");
  const btnEditLabel = editLabel || t("Bewerk beschrijving");

  const handleSave = async () => {
    if (trimmed.length > maxLength) return;
    setSaving(true);
    try {
      await onSave(trimmed);
      toast.success(trimmed ? t("Beschrijving opgeslagen") : t("Beschrijving verwijderd"));
      setOpen(false);
    } catch (err) {
      toast.error(t("Opslaan mislukt"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {!readOnly && (
        <button
          type="button"
          onClick={() => { setDraft(value || ""); setOpen(true); }}
          data-testid={`${testid}-open`}
          data-pdf-ignore="1"
          className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 mt-3"
        >
          {hasText ? <Pencil className="w-3.5 h-3.5" /> : <MessageSquare className="w-3.5 h-3.5" />}
          {hasText ? btnEditLabel : btnLabel}
        </button>
      )}

      {hasText && (
        <div
          data-testid={`${testid}-preview`}
          data-pdf-ignore="1"
          className="mt-3 border-l-4 border-primary bg-primary/5 px-4 py-3 text-sm text-foreground whitespace-pre-wrap"
        >
          {value}
        </div>
      )}

      {!readOnly && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid={`${testid}-dialog`}>
            <DialogHeader>
              <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2 pr-8">
                <MessageSquare className="w-5 h-5" /> {dialogTitle || t("Beschrijving toevoegen")}
              </DialogTitle>
              <DialogDescription>
                {t("Deze tekst verschijnt onder de grafiek in de PDF-export. Maximaal {n} tekens.").replace("{n}", String(maxLength))}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-2">
              <textarea
                value={draft}
                maxLength={maxLength}
                onChange={(e) => setDraft(e.target.value.slice(0, maxLength))}
                data-testid={`${testid}-textarea`}
                autoFocus
                rows={5}
                placeholder={t("Bijv. \"Uitgangspunt: inflatie 3%, geen nieuwe leningen na 2028\"")}
                className="w-full mt-1 border-2 border-foreground bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary resize-y"
              />
              <div className="text-xs text-muted-foreground text-right mt-1 font-mono-num" data-testid={`${testid}-counter`}>
                {draft.length} / {maxLength}
              </div>
            </div>
            <DialogFooter className="mt-2 gap-2 sm:justify-between">
              {onGenerate ? (
                <button
                  onClick={async () => {
                    setGenerating(true);
                    try {
                      const text = await onGenerate();
                      if (typeof text === "string" && text.trim()) {
                        setDraft(text.slice(0, maxLength));
                        toast.success(t("AI-samenvatting gegenereerd"));
                      }
                    } catch (err) {
                      toast.error(t("AI-samenvatting mislukt"));
                    } finally {
                      setGenerating(false);
                    }
                  }}
                  disabled={saving || generating}
                  data-testid={`${testid}-ai-generate`}
                  className="btn-brutal bg-accent text-accent-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-1.5"
                >
                  <Sparkles className={`w-4 h-4 ${generating ? "animate-pulse" : ""}`} /> {generating ? t("Bezig…") : (generateLabel || t("AI genereer"))}
                </button>
              ) : <span />}
              <div className="flex gap-2">
                <button
                  onClick={() => setOpen(false)}
                  disabled={saving}
                  data-testid={`${testid}-cancel`}
                  className="btn-brutal bg-background text-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-1.5"
                >
                  <X className="w-4 h-4" /> {t("Annuleren")}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid={`${testid}-save`}
                  className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-1.5"
                >
                  <Save className="w-4 h-4" /> {saving ? t("Bezig…") : t("Opslaan")}
                </button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
